/**
 * Settings — HX recomposition. The old monolithic page is split into
 * distinct HxCard modules: Profile, Plan, Preferences, Security,
 * Working hours, Seats, Integrations.
 */
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Buildings, GearSix, User, ShieldCheck, Clock, PuzzlePiece, Globe, ArrowUpRight, X, DeviceMobile } from "@phosphor-icons/react";
import ManageSeatsCard from "@/components/billing/ManageSeatsCard";
import TimezonePicker from "@/components/TimezonePicker";
import WorkingHoursEditor from "@/components/profile/WorkingHoursEditor";
import { detectBrowserTimezone } from "@/lib/timezones";
import { HxCard, HxCardHead, HxButton, HxChip, HxInput, HxLabel, HX_COLORS as C } from "@/hx";

export default function Settings() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPersonal = user?.organization?.type !== "business";
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tz, setTz] = useState(user?.timezone || detectBrowserTimezone());
  const [savingTz, setSavingTz] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [{ data: plans }, { data: sub }] = await Promise.all([
          api.get("/billing/v2/plans"),
          api.get("/billing/v2/subscription"),
        ]);
        const currentKey = sub?.subscription?.plan_key || plans?.current_plan;
        const row = (plans?.plans || []).find((p) => p.key === currentKey) || null;
        // Owner-Console stored the amount in dollars (not cents) for some
        // historic rows — normalise so we always display the correct number.
        const raw = row?.amount_minor;
        const dollars = typeof raw === "number" ? (raw < 200 ? raw : raw / 100) : null;
        setPlanInfo(row ? {
          name: row.name || currentKey || "—",
          amount: dollars != null ? `$${dollars.toFixed(0)}` : "—",
          interval: row.interval || "month",
          perSeat: row.billing_model === "per_seat",
        } : { name: currentKey || "—", amount: "—", interval: "month", perSeat: false });
      } catch {
        setPlanInfo(null);
      }
    })();
  }, [isAdmin]);

  const saveTimezone = async (newTz) => {
    setTz(newTz);
    setSavingTz(true);
    try {
      await api.patch("/me", { timezone: newTz });
      await refresh?.();
      toast.success(`Timezone saved: ${newTz}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save timezone");
    } finally {
      setSavingTz(false);
    }
  };

  const upgrade = async () => {
    if (!orgName.trim()) { setErr("Company name required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/upgrade-to-business", { org_name: orgName.trim() });
      await refresh?.();
      setUpgradeOpen(false);
      toast.success("Upgraded to Business — you can now invite teammates from the Team page.");
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="hbf-hx" data-testid="settings-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up">
        <div>
          <div className="hx-hero__eyebrow"><GearSix size={16} weight="duotone" color={C.blue} /> Workspace</div>
          <h1 className="hx-hero__title">Your <em>settings</em></h1>
          <p className="hx-hero__sub">{isAdmin ? "Manage your account, billing, and integrations." : "Manage your account."}</p>
        </div>
      </header>

      {isPersonal && (
        <HxCard className="hx-up" style={{ marginBottom: 20, background: "linear-gradient(165deg, #EAF1FF, #FFFFFF 65%)" }} data-testid="upgrade-business-banner">
          <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <Buildings size={26} weight="duotone" color={C.blue} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>Upgrade to a Business workspace</div>
              <div style={{ fontSize: 12.5, color: "var(--hx-ink-2)", marginTop: 2 }}>Invite teammates, get RBAC roles, and unlock the full Hello by Flow suite.</div>
            </div>
            <HxButton variant="primary" onClick={() => setUpgradeOpen(true)} data-testid="upgrade-business-btn">Upgrade</HxButton>
          </div>
        </HxCard>
      )}

      {upgradeOpen && (
        <div className="hx-scrim" data-testid="upgrade-modal">
          <div className="hx-modal">
            <div className="hx-modal__head">
              <div>
                <div style={{ fontSize: 10, letterSpacing: ".14em", color: "var(--hx-ink-3)", fontWeight: 800, textTransform: "uppercase" }}>Personal → Business</div>
                <div className="hx-modal__title">Create your company workspace</div>
              </div>
              <button onClick={() => setUpgradeOpen(false)} className="hx-btn hx-btn--ghost hx-btn--sm" data-testid="upgrade-modal-close"><X size={15} /></button>
            </div>
            <div className="hx-modal__body">
              <HxLabel>Company name</HxLabel>
              <HxInput autoFocus value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme Inc." data-testid="upgrade-org-name" />
              {err && <div style={{ fontSize: 12, color: "var(--hx-red)", marginTop: 8 }} data-testid="upgrade-error">{err}</div>}
              <HxButton variant="primary" onClick={upgrade} disabled={busy || !orgName.trim()} style={{ marginTop: 16, width: "100%", justifyContent: "center" }} data-testid="upgrade-submit">
                {busy ? "Upgrading..." : "Upgrade to Business"}
              </HxButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Row 1: Profile + Plan ────────────────────────────── */}
      <div className="hx-grid-main hx-up hx-d1" style={{ marginBottom: 20 }}>
        <HxCard className={isAdmin ? "hx-span7" : "hx-span12"} style={!isAdmin ? { gridColumn: "span 12" } : undefined} data-testid="settings-profile-card">
          <HxCardHead title="Profile" icon={<User size={15} weight="duotone" color={C.blue} />} />
          <div style={{ padding: "10px 24px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img src={user?.avatar_url} style={{ width: 60, height: 60, borderRadius: 18, objectFit: "cover", boxShadow: "var(--hx-sh-1)" }} alt="" />
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.015em" }}>{user?.name}</div>
                <div style={{ fontSize: 12, color: "var(--hx-ink-3)", fontFamily: "var(--hx-mono)" }}>{user?.email}</div>
                <HxChip tone={user?.role === "admin" ? "violet" : user?.role === "supervisor" ? "blue" : "gray"} style={{ marginTop: 6, textTransform: "capitalize" }}>{user?.role}</HxChip>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" style={{ marginTop: 22 }}>
              <Fact label="Member since" value={user?.created_at?.slice(0, 10)} />
              <Fact label="Workspace" value={user?.organization?.name || "Hello by Flow"} />
            </div>
          </div>
        </HxCard>

        {isAdmin && (
          <HxCard className="hx-span5" data-testid="settings-plan-card">
            <HxCardHead title="Plan" icon={<Buildings size={15} weight="duotone" color={C.violet} />} right={<HxChip tone="green">Live from Stripe</HxChip>} />
            <div style={{ padding: "8px 24px 24px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--hx-ink-3)" }}>Current tier</div>
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.03em", marginTop: 4 }} data-testid="settings-plan-name">{planInfo?.name || "—"}</div>
              <div style={{ fontSize: 13, color: "var(--hx-ink-2)", marginTop: 2 }} data-testid="settings-plan-price">
                {planInfo ? `${planInfo.amount}${planInfo.perSeat ? " / user" : ""} / ${planInfo.interval === "year" ? "yr" : "mo"}` : "Loading…"}
              </div>
              <HxButton to="/app/billing" style={{ marginTop: 18, width: "100%", justifyContent: "center" }} data-testid="manage-billing-btn">
                Manage billing <ArrowUpRight size={14} weight="bold" />
              </HxButton>
            </div>
          </HxCard>
        )}
      </div>

      {/* ── Row 2: Preferences + Security ────────────────────── */}
      <div className="hx-grid-main hx-up hx-d2" style={{ marginBottom: 20 }}>
        <HxCard className="hx-span7" data-testid="settings-preferences-card">
          <HxCardHead title="Preferences" icon={<Globe size={15} weight="duotone" color={C.green} />} right={savingTz && <span style={{ fontSize: 11, color: "var(--hx-ink-3)", fontFamily: "var(--hx-mono)" }}>Saving…</span>} />
          <div style={{ padding: "10px 24px 24px" }} data-testid="profile-tz-row">
            <HxLabel>Timezone</HxLabel>
            <TimezonePicker value={tz} onChange={saveTimezone} testid="profile-tz" />
            <div style={{ fontSize: 11, color: "var(--hx-ink-3)", marginTop: 8 }}>
              Used to render meeting times in your invites &amp; calendar downloads.
            </div>
          </div>
        </HxCard>

        <HxCard className="hx-span5" data-testid="settings-security-card">
          <HxCardHead title="Security" icon={<ShieldCheck size={15} weight="duotone" color={C.orange} />} />
          <div style={{ padding: "6px 24px 24px" }}>
            <div className="hx-snap" style={{ padding: "10px 0" }}>
              <span className="hx-snap__l"><ShieldCheck size={15} weight="duotone" color={C.blue} /> Email verification</span>
              <HxChip tone="green">Active</HxChip>
            </div>
            <div className="hx-snap" style={{ padding: "10px 0" }}>
              <span className="hx-snap__l"><DeviceMobile size={15} weight="duotone" color={C.blue} /> Trusted devices</span>
              <HxChip tone="blue">30-day trust</HxChip>
            </div>
            <HxButton to="/app/settings/sessions" size="sm" style={{ marginTop: 12, width: "100%", justifyContent: "center" }} data-testid="manage-sessions-btn">
              Manage active sessions <ArrowUpRight size={13} weight="bold" />
            </HxButton>
          </div>
        </HxCard>
      </div>

      {/* ── Working hours ────────────────────────────────────── */}
      <HxCard className="hx-up hx-d3" style={{ marginBottom: 20 }} data-testid="settings-working-hours-card">
        <HxCardHead title="Working hours" icon={<Clock size={15} weight="duotone" color={C.blue} />} />
        <div style={{ padding: "6px 24px 24px" }}>
          <WorkingHoursEditor
            initial={user?.working_hours}
            viewerTz={tz}
            onSaved={() => refresh?.()}
          />
        </div>
      </HxCard>

      {isAdmin && !isPersonal && (
        <div id="manage-seats" className="hx-up hx-d3" style={{ marginBottom: 20, scrollMarginTop: "80px" }}>
          <ManageSeatsCard />
        </div>
      )}

      {/* ── Integrations ─────────────────────────────────────── */}
      {isAdmin && (
        <HxCard className="hx-up hx-d4" data-testid="settings-integrations-card">
          <HxCardHead title="Integrations" icon={<PuzzlePiece size={15} weight="duotone" color={C.violet} />} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ padding: "10px 24px 24px" }}>
            {[
              { name: "HubSpot CRM", desc: "Sync contacts and call activity", status: "connected" },
              { name: "Salesforce", desc: "Log calls and recordings to Leads", status: "available" },
              { name: "Slack", desc: "Notify channels on missed calls", status: "available" },
              { name: "Zapier", desc: "Connect to 5,000+ apps", status: "available" },
              { name: "Google Calendar", desc: "Schedule callbacks", status: "connected" },
              { name: "Webhooks", desc: "Real-time call events", status: "available" },
            ].map((i) => (
              <div key={i.name} style={{ border: "1px solid var(--hx-line)", borderRadius: 14, padding: "14px 16px", transition: "box-shadow .2s, transform .2s" }} className="hover:shadow-md hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{i.name}</div>
                  <HxChip tone={i.status === "connected" ? "green" : "gray"}>{i.status}</HxChip>
                </div>
                <div style={{ fontSize: 12, color: "var(--hx-ink-2)", marginTop: 4 }}>{i.desc}</div>
              </div>
            ))}
          </div>
        </HxCard>
      )}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ borderBottom: "1px solid var(--hx-line-soft)", paddingBottom: 8 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--hx-ink-3)" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}
